import { PlusOutlined } from "@ant-design/icons";
import {
  Affix,
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
} from "antd";
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CHALLAN_LIST,
  TRANSPORT_PARTICULAR_OPTIONS_ENDPOINT,
} from "../../../apiServices/API_ENDPOINTS";
import { getData, postData, putData } from "../../../apiServices/common";
import HandleNotification from "../../../common/Notification";
import Header from "../../Common/Header";
import {
  DEFAULT_VEHICLE_TYPES,
  getLocalShipmentConfig,
  mergeVehicleTypes,
} from "./localShipmentConfig";

const { Option } = Select;

const mergeById = (list, item) =>
  item?.id && !list.some((existing) => existing?.id === item.id)
    ? [...list, item]
    : list;

const LocalShipmentForm = ({ editMode }) => {
  // Router
  const navigate = useNavigate();
  const location = useLocation();
  const { localShipmentId } = useParams();
  const config = getLocalShipmentConfig(location?.pathname);

  // States
  const [challanList, setChallanList] = useState([]);
  const [particularList, setParticularList] = useState([]);
  const [vehicleTypeList, setVehicleTypeList] = useState(DEFAULT_VEHICLE_TYPES);
  const [newVehicleTypeInput, setNewVehicleTypeInput] = useState("");
  // TODO: load gate pass options from the API once it is available
  const [gatePassList] = useState([]);
  const [saving, setSaving] = useState(false);

  // Antd
  const [form] = Form.useForm();

  const getChallanList = async (name) => {
    const response = await getData(CHALLAN_LIST, false, {
      ...(name && { name }),
    });

    if (response && response?.data?.code === 200) {
      setChallanList(response?.data?.data?.data || []);
    }
  };

  const getParticularList = async () => {
    const response = await getData(
      `${TRANSPORT_PARTICULAR_OPTIONS_ENDPOINT}?type=local`,
    );

    if (response && response?.data?.code === 200) {
      setParticularList((prev) =>
        (response?.data?.data || []).reduce(mergeById, prev),
      );
    }
  };

  const getVehicleTypes = async () => {
    const response = await getData(config.vehicleTypesApi);

    if (response && response?.data?.code === 200) {
      setVehicleTypeList((prev) =>
        mergeVehicleTypes(prev, ...(response?.data?.data || [])),
      );
    }
  };

  const getLocalShipment = useCallback(
    async (id) => {
      const response = await getData(`${config.api}/${id}`);

      if (response && response?.data?.code === 200) {
        const data = response?.data?.data;

        if (data?.challan_info) {
          setChallanList((prev) => mergeById(prev, data.challan_info));
        }
        if (data?.transport_particular) {
          setParticularList((prev) =>
            mergeById(prev, data.transport_particular),
          );
        }
        if (data?.vehicle_type) {
          setVehicleTypeList((prev) =>
            mergeVehicleTypes(prev, data.vehicle_type),
          );
        }

        form.setFieldsValue({
          vehicle_number: data?.vehicle_number,
          vehicle_type: data?.vehicle_type,
          vehicle_qty: data?.vehicle_qty,
          challan_id: data?.challan_id,
          gate_pass_no: data?.gate_pass_no,
          transport_particular_id: data?.transport_particular_id,
        });
      } else {
        HandleNotification(
          "error",
          "bottomRight",
          "Error getting data",
          null,
        );
      }
    },
    [config.api, form],
  );

  useEffect(() => {
    getChallanList();
    getParticularList();
    getVehicleTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (editMode && localShipmentId) {
      getLocalShipment(localShipmentId);
    }
  }, [editMode, localShipmentId, getLocalShipment]);

  const addVehicleType = () => {
    const type = newVehicleTypeInput?.trim();
    if (!type) return;

    const existing = vehicleTypeList.find(
      (item) => item.toLowerCase() === type.toLowerCase(),
    );
    setVehicleTypeList((prev) => mergeVehicleTypes(prev, type));
    form.setFieldsValue({ vehicle_type: existing || type });
    setNewVehicleTypeInput("");
  };

  const onFinish = async (values) => {
    const bodyData = {
      vehicle_number: values?.vehicle_number,
      vehicle_type: values?.vehicle_type,
      vehicle_qty: values?.vehicle_qty,
      challan_id: values?.challan_id,
      gate_pass_no: values?.gate_pass_no || null,
      transport_particular_id: values?.transport_particular_id,
    };

    setSaving(true);
    const response = editMode
      ? await putData(`${config.api}/${localShipmentId}`, bodyData)
      : await postData(config.api, bodyData);
    setSaving(false);

    if (response && response?.code === 200) {
      HandleNotification(
        "success",
        "bottomRight",
        `Successfully ${editMode ? "Updated" : "Added"}!`,
        null,
      );
      navigate(config.listPath);
    } else {
      HandleNotification(
        "error",
        "bottomRight",
        response?.message || "Something Went Wrong!",
        null,
      );
    }
  };

  return (
    <>
      <Affix offsetTop={0}>
        <Card className="p-fixed">
          <Header
            title={`${editMode ? "Update" : "Add"} Local ${config.label}`}
            actions={[
              <Affix offsetTop={20} key="discard">
                <Button
                  size="small"
                  danger
                  onClick={() => navigate(config.listPath)}
                >
                  Discard
                </Button>
              </Affix>,
              <Affix offsetTop={20} key="save">
                <Button
                  size="small"
                  type="primary"
                  loading={saving}
                  onClick={() => form.submit()}
                >
                  Save
                </Button>
              </Affix>,
            ]}
          />
        </Card>
      </Affix>
      <div className="containt-body pt-3">
        <Form layout="vertical" form={form} onFinish={onFinish}>
          <Card>
            <Row gutter={6}>
              <Col span={4}>
                <Form.Item
                  label="Vehicle Number"
                  name="vehicle_number"
                  rules={[
                    { required: true, message: "Please write Vehicle Number" },
                  ]}
                >
                  <Input placeholder="Vehicle Number" size="small" />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Type of Vehicle"
                  name="vehicle_type"
                  rules={[
                    {
                      required: true,
                      message: "Please select Type of Vehicle",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Type of Vehicle"
                    size="small"
                    filterOption={(input, option) =>
                      String(option?.children || "")
                        .toLowerCase()
                        .includes(String(input).toLowerCase())
                    }
                    dropdownRender={(menu) => (
                      <>
                        {menu}
                        <Divider style={{ margin: "8px 0" }} />
                        <Space style={{ padding: "0 8px 4px", display: "flex" }}>
                          <Input
                            placeholder="New type"
                            size="small"
                            maxLength={20}
                            value={newVehicleTypeInput}
                            onChange={(e) =>
                              setNewVehicleTypeInput(e.target.value)
                            }
                            onKeyDown={(e) => {
                              e.stopPropagation();
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addVehicleType();
                              }
                            }}
                          />
                          <Button
                            type="text"
                            size="small"
                            icon={<PlusOutlined />}
                            onClick={addVehicleType}
                            disabled={!newVehicleTypeInput?.trim()}
                          >
                            Add
                          </Button>
                        </Space>
                      </>
                    )}
                  >
                    {vehicleTypeList.map((type) => (
                      <Option key={type} value={type}>
                        {type}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Vehicle Quantity"
                  name="vehicle_qty"
                  rules={[
                    {
                      required: true,
                      message: "Please write Vehicle Quantity",
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Vehicle Quantity"
                    size="small"
                    min={1}
                    precision={0}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Challan No."
                  name="challan_id"
                  rules={[
                    { required: true, message: "Please select Challan No." },
                  ]}
                >
                  <Select
                    showSearch
                    allowClear
                    placeholder="Search to Select"
                    size="small"
                    filterOption={false}
                    onSearch={(value) => getChallanList(value)}
                  >
                    {challanList.map((challan) => (
                      <Option key={challan?.id} value={challan?.id}>
                        {challan?.challan_no || ""}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item label="Gate Pass No." name="gate_pass_no">
                  <Select
                    showSearch
                    allowClear
                    placeholder="Select Gate Pass No."
                    optionFilterProp="children"
                    size="small"
                  >
                    {gatePassList.map((gatePass) => (
                      <Option key={gatePass} value={gatePass}>
                        {gatePass}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={4}>
                <Form.Item
                  label="Raj Paribahan Type"
                  name="transport_particular_id"
                  rules={[
                    {
                      required: true,
                      message: "Please select Raj Paribahan Type",
                    },
                  ]}
                >
                  <Select
                    showSearch
                    placeholder="Raj Paribahan Type"
                    optionFilterProp="children"
                    size="small"
                  >
                    {particularList.map((particular) => (
                      <Option key={particular?.id} value={particular?.id}>
                        {particular?.particular}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Card>
        </Form>
      </div>
    </>
  );
};

export default LocalShipmentForm;
